import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { TimeRange } from "@/routes/insights/insights";
import { type Timestamp } from "@bufbuild/protobuf";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Resolution } from "gitpod-next-api/gitpod/v1/usage_pb";

export const insightsQueryKey = {
    mostActiveUsers: () => keyWithPrincipal(["insights", "mostActiveUsers"]),
    activeUsersTimeSeries: () => keyWithPrincipal(["insights", "activeUsersTimeSeries"]),
    activeUsersCount: () => keyWithPrincipal(["insights", "activeUsersCount"]),
};

export const useMostActiveUsers = (startTimestamp: Timestamp, endTimestamp: Timestamp, selectedRange: TimeRange) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useInfiniteQuery({
        queryKey: [...insightsQueryKey.mostActiveUsers(), selectedRange.days],
        queryFn: ({ pageParam = "" }) =>
            api.usageService.getTopActiveUsers({
                dateRange: {
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                },
                pagination: {
                    token: pageParam,
                },
            }),
        getNextPageParam: (lastPage) => lastPage.pagination?.nextToken || undefined,
        initialPageParam: "",
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        staleTime: 1_000 * 60 * 60,
        gcTime: 1_000 * 60 * 60,
    });
};

export const useActiveUsersCount = (startTimestamp: Timestamp, endTimestamp: Timestamp, selectedRange: TimeRange) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data, isLoading, error } = useQuery({
        queryKey: [...insightsQueryKey.activeUsersCount(), selectedRange.days],
        queryFn: () =>
            api.usageService.getActiveUsersCount({
                dateRange: {
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                },
            }),
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        staleTime: 1_000 * 60 * 60,
        gcTime: 1_000 * 60 * 60,
    });

    return { data, isLoading, error };
};

export const useActiveUsersTimeSeries = (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
    selectedRange: TimeRange,
) => {
    const api = useGitpodAPI();

    const { data, isLoading, error } = useQuery({
        queryKey: [...insightsQueryKey.activeUsersTimeSeries(), selectedRange.days],
        queryFn: () =>
            api.usageService.getActiveUsersTimeSeries({
                dateRange: {
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                },
                resolution: calculateResolution(selectedRange.days),
            }),
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        // enabled: !!user, // todo(ft): enable once the method is implemented
        enabled: false,
        staleTime: 1_000 * 60 * 60 * 12,
        gcTime: 1_000 * 60 * 60 * 24,
    });

    return { data, isLoading, error };
};

/**
 * @returns the appropriate resolution for the given date range.
 */
const calculateResolution = (days: number): Resolution => {
    const weeks = days / 7;
    if (weeks > 100) {
        return Resolution.MONTHLY;
    }
    if (days > 100) {
        return Resolution.WEEKLY;
    }
    if (days * 24 > 100) {
        return Resolution.DAILY;
    }

    return Resolution.HOURLY;
};
